import React, { createContext, useContext, useState, useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import Logger from '../utils/logger.js';
import { apiClient } from '../services/apiClient.js';
import { detectTheme, watchSystemTheme } from '../utils/themeDetection.js';

/**
 * Theme Context for managing application theme state with Progressive Enhancement
 * Supports server-side rendered themes, localStorage, and CSS-only fallbacks
 */
const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: _theme => {},
  isJSEnabled: true
});

/**
 * Custom hook to use the Theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Progressive Enhancement Theme Provider
 * Works with or without JavaScript, supports server-side themes
 */
export const ThemeProvider = ({ children }) => {
  // Initialize theme using shared detection utility
  const [theme, setThemeState] = useState(() => detectTheme());
  const [isJSEnabled] = useState(true); // JavaScript is enabled if this runs
  const isAuthenticated = useIsAuthenticated();

  // NOTE: Theme styles are in main CSS bundle with [data-theme] selectors (css-variables.css)
  // Dynamic CSS loading (Phase 3) was evaluated but deemed unnecessary:
  // - Theme CSS is minimal (~50 lines per theme)
  // - Current approach via [data-theme] attribute works efficiently
  // - Dynamic loading would add complexity for negligible bundle savings

  // Apply theme to document and persist preferences
  useEffect(() => {
    // Apply theme to HTML element for CSS variables
    document.documentElement.setAttribute('data-theme', theme);

    // Apply dark class to html element for Tailwind dark mode
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Also apply to body for backwards compatibility
    document.body.className = theme;

    // Persist to localStorage for client-side preference
    try {
      localStorage.setItem('theme', theme);
    } catch (error) {
      Logger.warn('Failed to save theme to localStorage:', error);
    }

    // Send theme preference to server for database persistence
    // Only save to server if user is authenticated
    if (isAuthenticated) {
      try {
        apiClient.post('/users/theme', { theme }).catch(error => {
          Logger.warn('[ThemeContext] Failed to persist theme to server', { error: error.message });
        });
      } catch (_error) {
        // Ignore server persistence errors
      }
    }
  }, [theme, isAuthenticated]);

  // Watch for system theme changes using shared utility
  useEffect(() => {
    const cleanup = watchSystemTheme(newTheme => {
      // Only auto-switch if user hasn't manually set a preference
      const hasUserPreference = localStorage.getItem('theme');

      if (!hasUserPreference) {
        setThemeState(newTheme);
      }
    });

    return cleanup;
  }, []);

  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);
  };

  /**
   * Set theme to a specific value
   * @param {string} newTheme - 'light' or 'dark'
   */
  const setTheme = newTheme => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
    }
  };

  const value = {
    theme,
    toggleTheme,
    setTheme,
    isJSEnabled
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
